import {
  API_ROUTES,
  formatMinor,
  monthlyFlowSchema,
  reportRankRowSchema,
  type MonthlyFlow,
  type ReportRankRow,
} from '@hvac/contracts';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  HBarList,
  MonthBars,
  PageHeader,
  Skeleton,
  StatBlock,
} from '@hvac/ui';
import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient, sessionQuery } from '../../shared/api/queries.js';
import { LedgerCard } from './ledger-card.js';
import { ProfitAndLossCard } from './profit-and-loss-card.js';

/**
 * Reports.
 *
 * The constitution's rule governs every card here: reports explain, not
 * merely display. Each chart is paired with a sentence computed from the same
 * rows it draws, what changed, and whether to care, and every figure comes
 * out of SQL over confirmed records and booked jobs. A chart never averages,
 * estimates, or invents; it is the ledger, made glanceable.
 */

const flowsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['reports', organizationId, 'flows'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.reports.monthlyFlows(organizationId),
        schema: z.array(monthlyFlowSchema),
        signal,
      }),
    staleTime: 60_000,
  });

const rankedQuery = (organizationId: string, kind: 'topCategories' | 'topSuppliers') =>
  queryOptions({
    queryKey: ['reports', organizationId, kind] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.reports[kind](organizationId),
        schema: z.array(reportRankRowSchema),
        signal,
      }),
    staleTime: 60_000,
  });

const sum = (values: readonly string[]): bigint =>
  values.reduce((total, value) => total + BigInt(value), 0n);

const monthLabel = (iso: string): string =>
  new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-CA', {
    month: 'short',
    timeZone: 'UTC',
  });

/** The sentence beside the year chart: what the months actually say. */
function flowsInsight(flows: readonly MonthlyFlow[]): string {
  const earned = sum(flows.map((flow) => flow.earnedMinor));
  const spent = sum(flows.map((flow) => flow.spentMinor));
  const booked = sum(flows.map((flow) => flow.bookedMinor));

  if (earned === 0n && spent === 0n && booked > 0n) {
    const busiest = flows.reduce((best, flow) =>
      BigInt(flow.bookedMinor) > BigInt(best.bookedMinor) ? flow : best,
    );
    return (
      `${formatMinor(booked.toString())} of work is booked across the year, ` +
      `heaviest in ${monthLabel(busiest.month)}. Earned and spent fill in as records are confirmed.`
    );
  }
  if (earned === 0n && spent === 0n) {
    return 'The chart draws itself as records are confirmed, nothing here is estimated.';
  }
  const kept = earned - spent;
  return (
    `Earned ${formatMinor(earned.toString())}, spent ${formatMinor(spent.toString())}, ` +
    `${kept < 0n ? formatMinor((-kept).toString()) + ' more out than in' : formatMinor(kept.toString()) + ' kept'} ` +
    'over twelve months, from confirmed records only.'
  );
}

function rankedInsight(rows: readonly ReportRankRow[], noun: string): string | null {
  if (rows.length < 2) return null;
  const total = sum(rows.map((row) => row.totalMinor));
  const top = rows[0];
  if (top === undefined || total === 0n) return null;
  const share = Number((BigInt(top.totalMinor) * 100n) / total);
  return share >= 40
    ? `${top.label} is ${String(share)}% of ${noun} shown here, worth knowing before that relationship changes.`
    : null;
}

export function ReportsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';

  const flows = useQuery(flowsQuery(organizationId));
  const categories = useQuery(rankedQuery(organizationId, 'topCategories'));
  const suppliers = useQuery(rankedQuery(organizationId, 'topSuppliers'));

  const flowRows = flows.data ?? [];
  const earnedYtd = sum(flowRows.map((flow) => flow.earnedMinor));
  const spentYtd = sum(flowRows.map((flow) => flow.spentMinor));
  const bookedNow = sum(flowRows.map((flow) => flow.bookedMinor));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Business"
        title="Reports"
        description="What changed, and whether to care. Every figure is computed from confirmed records and booked jobs, nothing here is estimated."
      />

      {flows.isError ? (
        <Alert tone="danger" title="The reports could not be computed">
          {flows.error.message}
        </Alert>
      ) : null}

      <div className="stagger-rise grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatBlock
          label="Booked, not yet earned"
          value={formatMinor(bookedNow.toString())}
          hint="Quoted open jobs"
        />
        <StatBlock
          label="Earned · 12 months"
          value={formatMinor(earnedYtd.toString())}
          hint="Confirmed revenue"
        />
        <StatBlock
          label="Spent · 12 months"
          value={formatMinor(spentYtd.toString())}
          hint="Confirmed expenses"
        />
        <StatBlock
          label="Kept"
          value={formatMinor((earnedYtd - spentYtd).toString())}
          {...(earnedYtd - spentYtd < 0n ? { tone: 'danger' as const } : {})}
          hint="Earned minus spent"
        />
      </div>

      <Card>
        <CardHeader
          title="The year"
          description={flows.data === undefined ? 'Computing…' : flowsInsight(flows.data)}
        />
        <CardBody>
          {flows.isPending ? (
            <Skeleton className="h-44 w-full" />
          ) : (
            <MonthBars
              months={flowRows.map((flow) => monthLabel(flow.month))}
              series={[
                {
                  label: 'Earned',
                  colorClass: 'bg-success',
                  values: flowRows.map((flow) => Number(flow.earnedMinor)),
                },
                {
                  label: 'Spent',
                  colorClass: 'bg-brand-500',
                  values: flowRows.map((flow) => Number(flow.spentMinor)),
                },
                {
                  label: 'Booked',
                  colorClass: 'bg-brand-200',
                  values: flowRows.map((flow) => Number(flow.bookedMinor)),
                },
              ]}
              formatValue={(minor) => formatMinor(String(minor))}
            />
          )}
        </CardBody>
      </Card>

      <ProfitAndLossCard organizationId={organizationId} />

      <LedgerCard organizationId={organizationId} />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Where the money goes"
            description={
              rankedInsight(categories.data ?? [], 'spending') ??
              'Confirmed expense lines, by account, last twelve months.'
            }
          />
          <CardBody>
            {categories.isPending ? (
              <Skeleton className="h-40 w-full" />
            ) : (categories.data ?? []).length === 0 ? (
              <EmptyState
                icon="bookkeeping"
                title="No confirmed spending yet"
                body="This chart draws itself from confirmed records. Drop receipts in the Inbox, approve them in Review, and the accounts appear here ranked."
              />
            ) : (
              <HBarList
                items={(categories.data ?? []).map((row) => ({
                  label: row.label,
                  ...(row.sublabel === null ? {} : { sublabel: row.sublabel }),
                  value: Number(row.totalMinor),
                }))}
                formatValue={(minor) => formatMinor(String(minor))}
              />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Who you buy from"
            description={
              rankedInsight(suppliers.data ?? [], 'supplier spend') ??
              'Confirmed spend by supplier, last twelve months.'
            }
          />
          <CardBody>
            {suppliers.isPending ? (
              <Skeleton className="h-40 w-full" />
            ) : (suppliers.data ?? []).length === 0 ? (
              <EmptyState
                icon="suppliers"
                title="No confirmed supplier spend yet"
                body="As receipts are approved, suppliers rank themselves here, and a supplier growing past 40% of spend earns a sentence saying so."
              />
            ) : (
              <HBarList
                items={(suppliers.data ?? []).map((row) => ({
                  label: row.label,
                  value: Number(row.totalMinor),
                }))}
                formatValue={(minor) => formatMinor(String(minor))}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
